import React, {useEffect, useState} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight, Award, LineChart } from 'lucide-react';

const members = [
    {
        id: 1,
        name: "R.S. PARTHASARATHY",
        role: "Founder & Chairman | Global Mentor",
        img: '/assets/profile/profile01.webp',
        detailedBio: "Mr. R S Parthasarathy is the visionary mind and primary architect behind Fortune Signals’ rare and proprietary market-timing methodologies.\n" +
            "With several decades of deep research across global financial markets, cyclical behaviour, astro-financial patterns, universal time harmonics, and institutional forecasting models, he stands as one of the very few specialists in the world who has mastered pure time-based trading science.\n" +
            "As the main leader, chief strategist, and global mentor, he personally guides traders in India, UAE, Europe, UK, and Asia, transmitting knowledge that is almost impossible to find in traditional trading education.\n",
        specialty: "Institutional Psychology",
    },
    {
        id: 2,
        name: "BHAGESH GIRI",
        role: "Co-Founder | Managing Director",
        img: "/assets/profile/Profile2.webp",
        detailedBio: "Mr. Bhagesh Giri is the strategic mastermind behind Fortune Signals’ global operations, course design, and international trader community. With extensive experience in market research, predictive time cycles, and global trading systems, he has engineered the frameworks that make Fortune Signals a world-class trading academy. His vision is to create a global platform where traders are empowered with knowledge, precision, and community support, enabling them to master the markets with time-driven intelligence rather than price dependence.",
        specialty: "Global Operations",
    },
    {
        id: 3,
        name: "Vinayakk V Joshi",
        role: "Director – Dubai | Trading Mentor",
        img: "/assets/profile/vinayak.jpeg",
        detailedBio: "Mr. Vinayakk V Joshi is the driving force behind Fortune Signals’ global expansion and the face of our international education ecosystem. \n" +
            "Based in Dubai, he acts as the single-point mentor, advisor, and strategic guide for traders across the UAE, GCC, Europe, UK, and Asia.\n" +
            "With deep expertise in Time Technique Trading, Predictive Time Cycles, and Astro-Market Analysis, he plays a pivotal role in bringing these rare methodologies to international traders who seek precision over speculation.\n",
        specialty: "Astro-Market Analysis",
    }
];

const Team = () => {
    const [selectedMember, setSelectedMember] = useState(null);

    // Prevent scroll when modal open
    useEffect(() => {
        if (selectedMember) {
            const scrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = "100%";
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            window.scrollTo(0, parseInt(scrollY || "0") * -1);
        }
    }, [selectedMember]);

    return (
        <section
            id="team"
            className="relative w-full py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-12 font-poppins overflow-hidden"
        >
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary/5 rounded-full blur-[60px] md:blur-[100px] pointer-events-none"></div>

            <div className="relative z-10 mx-auto w-full sm:w-[90%] md:w-[90%] lg:w-[95%] xl:w-[85%] max-w-7xl">
                {/* Header */}
                <div className="lg:text-center mb-12 sm:mb-16 md:mb-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            Meet the Team
                        </span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-dark dark:text-light leading-[1.1]"
                    >
                        The Minds <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Behind the Charts</span>
                    </motion.h2>
                </div>

                {/* Grid */}
                <div className="grid gap-8 lg:gap-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {members.map((member, index) => (
                        <MemberCard
                            key={member.id}
                            member={member}
                            index={index}
                            onClick={() => setSelectedMember(member)}
                        />
                    ))}
                </div>
            </div>

            {/* Modal Popup */}
            <AnimatePresence>
                {selectedMember && (
                    <MemberModal
                        member={selectedMember}
                        close={() => setSelectedMember(null)}
                    />
                )}
            </AnimatePresence>

        </section>
    );
};

// --- Sub-Component: The Card ---
const MemberCard = ({ member, index, onClick }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group relative cursor-pointer flex flex-col h-full"
            onClick={onClick}
        >
            {/*
                FIX 1: Image Aspect Ratio
                aspect-[3/4] is good, but on very small screens,
                we ensure object-top is active to keep the face visible.
            */}
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-6 border border-gray-200 dark:border-white/5 shadow-lg group-hover:shadow-2xl group-hover:shadow-primary/20 transition-all duration-500">
                <img
                    src={member.img}
                    alt={member.name}
                    className="
                        duration-700 filter
                        border-[6px] border-white dark:border-neutral-800 rounded-2xl
                        w-full h-full object-cover object-top
                        group-hover:scale-105 transition-transform
                    "
                />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 translate-y-10 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 w-max">
                    <button className="px-5 py-2 bg-white text-primary text-sm font-bold rounded-full shadow-lg flex items-center gap-2">
                        View Profile <ArrowUpRight size={16} />
                    </button>
                </div>
            </div>

            <div className="text-center mt-auto">
                <h3 className="text-xl sm:text-2xl font-bold text-dark dark:text-light mb-1 group-hover:text-primary transition-colors">
                    {member.name}
                </h3>
                <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-2">
                    {member.role}
                </p>
                <div className="w-12 h-1 bg-primary/20 mx-auto rounded-full group-hover:w-24 group-hover:bg-primary transition-all duration-500"></div>
            </div>
        </motion.div>
    );
};

// --- Sub-Component: The Modal ---
const MemberModal = ({ member, close }) => {
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) close();
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-6 bg-dark/60 backdrop-blur-md"
            onClick={handleBackdropClick}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="
                    relative w-full max-w-4xl
                    /* FIX 2: Height Handling for Mobiles */
                    h-[100dvh] sm:h-auto sm:max-h-[90vh]
                    overflow-y-auto overflow-x-hidden scrollbar-none
                    bg-white dark:bg-[#0F1624]
                    sm:rounded-3xl shadow-2xl border border-gray-200 dark:border-white/10
                    flex flex-col md:flex-row
                "
            >
                {/* Close Button */}
                <button
                    onClick={close}
                    className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/80 dark:bg-black/50 text-dark dark:text-light hover:bg-danger hover:text-white transition-colors shadow-sm"
                >
                    <X size={20} />
                </button>

                {/*
                   FIX 3: Modal Image Logic
                   - OLD: h-64 (Forces landscape, cuts off head/body)
                   - NEW: aspect-square (Square) or aspect-[4/3] on Mobile.
                   - This ensures the image is tall enough to show the person.
                */}
                <div className="w-full md:w-2/5 shrink-0 relative aspect-square sm:aspect-[4/3] md:aspect-auto md:h-auto">
                    <img
                        src={member.img}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/20"></div>
                </div>

                {/* Right Side: Details */}
                <div className="w-full md:w-3/5 p-6 sm:p-8 md:p-12 flex flex-col justify-center">

                    <div className="mb-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                            <Award size={14} /> {member.role}
                        </span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark dark:text-light mb-4">
                        {member.name}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-6 border-l-4 border-primary/50 pl-4 whitespace-pre-line">
                        {member.detailedBio}
                    </p>

                    {/* Expertise Box */}
                    <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 mb-2 md:mb-8 flex items-start gap-4 border border-gray-100 dark:border-white/5">
                        <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                            <LineChart className="text-primary w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold">Key Specialty</p>
                            <p className="text-sm md:text-base text-dark dark:text-light font-medium">{member.specialty}</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Team;